import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StorageService } from '../services';
import { CommunicationType } from '../models/enums';
import {
  parseMessagesFiltersFromStorage,
  parseSuggestionsFiltersFromStorage,
  resolveSuggestionsSort,
  serializeMessagesFilters,
  serializeSuggestionsFilters,
  serializeSuggestionsSort,
} from '../filters';

/**
 * Hydrates the URL from localStorage *before* the resolver runs, so the data
 * fetch happens only once per sidebar entry. See {@link bookingsHydrationGuard}.
 *
 * Reads the communications type from the route config path (`suggestions/...`
 * or `messages/...`) to decide which storage to consult.
 */
export const communicationsHydrationGuard: CanActivateFn = async (route, state) => {
  if (route.queryParamMap.keys.length > 0) return true;

  const storage = inject(StorageService);
  const router = inject(Router);

  const type = route.routeConfig?.path?.split('/')[0] as CommunicationType;

  const queryParams: { [key: string]: string | number } = {};

  if (type === CommunicationType.SUGGESTIONS) {
    const stored = parseSuggestionsFiltersFromStorage(await storage.getSuggestionsFilters());
    if (stored) {
      const serialized = serializeSuggestionsFilters(stored);
      for (const [k, v] of Object.entries(serialized)) {
        if (v != null && v !== '') queryParams[k] = v as string;
      }
    }
    const storedSort = resolveSuggestionsSort(
      route.queryParamMap,
      await storage.getSuggestionsSortType(),
      await storage.getSuggestionsSortDirection(),
    );
    for (const [sortParam, sortValue] of Object.entries(serializeSuggestionsSort(storedSort))) {
      if (sortValue != null) queryParams[sortParam] = sortValue;
    }
  } else if (type === CommunicationType.MESSAGES) {
    const stored = parseMessagesFiltersFromStorage(await storage.getMessagesFilters());
    if (stored) {
      const serialized = serializeMessagesFilters(stored);
      for (const [k, v] of Object.entries(serialized)) {
        if (v != null && v !== '') queryParams[k] = v as string;
      }
    }
  }

  if (Object.keys(queryParams).length === 0) return true;

  const path = state.url.split('?')[0];
  return router.createUrlTree([path], { queryParams });
};
